import React, { useEffect, useState } from 'react';

/**
 * Hook returning live system telemetry (MEM, NET, FPS) and a session uptime
 * formatted as HH:MM:SS based on a stable startMs timestamp.
 */
export const useSystemTelemetry = (startMs) => {
  const [stats, setStats] = useState({ mem: 0, net: 0, fps: 0 });
  const [uptime, setUptime] = useState('00:00:00');
  const [localClock, setLocalClock] = useState('');

  // FPS measurement via rAF
  useEffect(() => {
    let frame = 0;
    let last = performance.now();
    let raf;
    const loop = (now) => {
      frame++;
      if (now - last >= 1000) {
        const fps = Math.min(99, Math.round((frame * 1000) / (now - last)));
        setStats(prev => ({ ...prev, fps }));
        frame = 0;
        last = now;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // MEM + NET poll every 1.4s
  useEffect(() => {
    const tick = () => {
      // Memory (Chromium): used vs limit
      let mem = 0;
      if (performance && performance.memory && performance.memory.jsHeapSizeLimit) {
        mem = Math.round(
          (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100
        );
      }
      // Network: downlink Mbps (cap at 100Mbps mapped to 100%)
      let net = 0;
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && typeof conn.downlink === 'number') {
        net = Math.min(100, Math.round((conn.downlink / 100) * 100));
      }
      setStats(prev => ({ ...prev, mem, net }));
    };
    tick();
    const id = setInterval(tick, 1400);
    return () => clearInterval(id);
  }, []);

  // Uptime clock based on startMs + wall-clock local time
  useEffect(() => {
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (sec) => {
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60);
      const s = sec % 60;
      return `${pad(h)}:${pad(m)}:${pad(s)}`;
    };
    const tick = () => {
      const now = new Date();
      setUptime(fmt(Math.floor((Date.now() - startMs) / 1000)));
      setLocalClock(`${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startMs]);

  return { stats, uptime, localClock };
};

export default useSystemTelemetry;
